<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="600">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>Add Location to "{{ company.company_name }}" Company</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container>
                <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy" @submit.prevent="addLocation">
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['name']" :rules="[vRules.required,vRules.maxLength(40)]"
                                          autofocus density="compact" label="Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_1']" :rules="[vRules.required]" density="compact"
                                          label="Address 1"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_2']" density="compact" label="Address 2"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_city']" :rules="[vRules.required]" density="compact"
                                          label="City"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_state']" :rules="[vRules.required]" density="compact"
                                          label="State"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_postalcode']" :rules="[vRules.required]"
                                          density="compact" label="Postal Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_country']" :rules="[vRules.required]"
                                          density="compact" label="Country"/>
                        </v-col>

                        <v-col class="pb-0" cols="12">
                            <div class="text-subtitle-2 mb-1">Alerts</div>
                            <v-btn-toggle v-model="formData['enter_or_exit']" color="primary" density="compact"
                                          divided mandatory variant="outlined">
                                <v-btn v-for="option in placeAlertOptions" :key="option.value" :value="option.value"
                                       size="small">{{ option.title }}
                                </v-btn>
                            </v-btn-toggle>
                            <div class="text-caption text-grey mt-1">{{ placeAlertHint(formData['enter_or_exit']) }}</div>
                        </v-col>
                        <v-col cols="12" style="height: 300px">
                            <l-map ref="mapContainer"
                                   :center="[(formData.latitude??47.41322), (formData.longitude??-1.219482)]" :zoom="10"
                                   @ready="mapIsReady">
                                <l-tile-layer :url="$mapTiles.street"/>
                            </l-map>
                        </v-col>

                        <v-col class="py-0 text-right" cols="12">
                            <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                        </v-col>
                    </v-row>

                </v-form>
            </v-container>
        </v-card>
    </v-dialog>
</template>

<script setup>
import "leaflet-draw";
import L from "leaflet";
import {reactive, ref} from "vue";
import "leaflet/dist/leaflet.css";
import {send} from "@/composables/apiClient";
import {placeAlertHint, placeAlertOptions} from "@/composables/placeAlerts";
import "leaflet-geosearch/assets/css/leaflet.css";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useOnboardingStore} from "@/stores/onboardingStore";
import {LMap, LTileLayer, LMarker} from "@vue-leaflet/vue-leaflet";
import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {mapTiles} from "@/composables/mapConfig";

const emits = defineEmits(['update:modelValue', 'updateLocations']);
const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    company: Object,
    modelValue: Boolean,
});
const mapContainer = ref();
const errors = ref([]);
const dialogSwitch = ref(props.modelValue);
const formData = reactive({
    valid: false,
    name: null,
    addr_1: null,
    addr_2: null,
    addr_city: null,
    addr_state: null,
    addr_country: null,
    addr_postalcode: null,
    enter_or_exit: 3,
    polygon_coordinates: null,
});

function mapIsReady() {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();
    const geoSearchOptions = new GeoSearchControl({
        style: "bar",
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel: "Enter address",
        provider: new OpenStreetMapProvider({searchUrl: mapTiles.searchUrl, reverseUrl: mapTiles.reverseUrl}),
    });

    map.addControl(geoSearchOptions);
    /*Add Draw Tool Bar Control*/
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: false,
            polygon: {
                showArea: true,
                allowIntersection: false, // Restricts shapes to simple polygons
                drawError: {
                    color: '#9d1212', // Color the shape will turn when intersects
                    message: '<strong>Oh snap!<strong> you can\'t draw that!' // Message that will show when intersect
                }
            },
            // disable toolbar item by setting it to false
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {
            featureGroup: drawnItems
        }
    });
    map.addControl(drawControl);

    map.on('draw:created', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        if (e.layerType === 'polygon') {
            formData['polygon_coordinates'] = e.layer.getLatLngs()[0];
        }
        drawnItems.addLayer(e.layer);
    });

    map.on('draw:edited', function (e) {
        e.layers.eachLayer(function (layer) {
            if (layer instanceof L.Polygon) {
                formData['polygon_coordinates'] = layer.getLatLngs()[0];
            }
        });
    });

    map.on('draw:deleted', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        formData['polygon_coordinates'] = null;
    });
}

const addLocation = () => {
    errors.value = [];
    send('post', `companies/locations/${props.company.id}`, formData)
        .then((response) => {
            showSnackbar(response.data.message);
            if (response.data.success) {
                useOnboardingStore().markLocationAdded();
                emits('updateLocations', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            console.log(errorResponse)
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
</script>
